import { yupResolver } from '@hookform/resolvers/yup';
import * as Accordion from '@radix-ui/react-accordion';
import { AxiosError, AxiosInstance } from 'axios';
import { addMonths } from 'date-fns';
import { FiUploadCloud } from 'react-icons/fi';
import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { errorMessageAdapter } from '../../../adapters';
import { MainCustomButton } from '../../../components/Buttons';
import { FormPhoneInput } from '../../../components/Form/FormPhoneInput';
import { StepperItem } from '../../../components/Stepper';
import { DashboardContainer, Input, PageHeadline } from '../../../components/atoms';
import { CustomInput } from '../../../components/inputs';
import { countries } from '../../../constants/objects';
import i18n from '../../../plugins/i18n/i18n';
import { AccountService } from '../../../services/AccountService';
import { DirectorsRegisteringService } from '../../../services/DirectorsService';
import { useAuth } from '../../../store/auth';
import { formatDateToYYYYMMDD } from '../../../utils/date';
import { errorToast } from '../../../utils/toasts';
import { AccordionContent, AccordionItem, AccordionTrigger } from './components/RadixAccordion';
import { Steps, useRegisteringStore } from './store/registering.store';
import { useApi } from '../../../services/hooks/apiHook';
import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';
import { defaultColors } from '../../../styles/style-guide';
import { ShareholdersRegisteringService, type registerDocumentsShareholders } from '../../../services/ShareholdersService';
import { FormValues, UserInfos } from './Interfaces';
import {
  isAllowedShareholderFileSize,
  isAllowedShareholderFileType,
  hasShareholderDocument,
  mapShareholderDataToUserInfos,
  shareholderFields,
  buildRegisteringPeopleFormValues,
  countryCodeFromDdi
} from './utils/mapShareholdersData';
import { DocumentMockup } from './components/DocumentMockup';
import { ScanPassportControl } from './components/PassportMrzScan';

enum AccordionState {
  VALIDATED,
  CURRENT,
  PENDING
}

export interface FormDataTypeRegisterDirectors {
  id?: string;
  index: number;
  name: string;
  occupation: string;
  gender: string;
  dateBirth: string;
  phone: string;
  ddi: string;
  email: string;
  nationality: string;
  passportNo: string;
  expiryDate: string;
  passportIssuingCountry: string;
  issueDate: string;
  address1: string;
  address2: string;
  postalCode: string;
  city: string;
  state: string;
  neighborhood: string;
  country: string;
  passport: File;
  document1: File;
  proveAddress: File | null;
}

const today = new Date();
const sixMonthsFromNow = addMonths(today, 6);
const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

const DashboardRegisteringDirectorsPage: React.FunctionComponent<any> = () => {
  const [shareholdersData, setShareholdersData] = useState<any>([]);
  const [fileNames, setFileNames] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [accordionStates, setAccordionStates] = useState<any>([]);
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [currentAccordion, setCurrentAccordion] = useState<string>('item-0');
  const [activeShareholderIndex, setActiveShareholderIndex] = useState<number>(0);
  const [numberDirectors, setNumberDirectors] = useState<number>();
  const [numberRegistredDirectors, setNumberRegistredDirectors] = useState<number>(0);

  const { token, company } = useAuth();
  const { setStep } = useRegisteringStore();
  const { i18n } = useTranslation();
  const api: AxiosInstance = useApi();

  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);

  const primaryColor = colors.primary || defaultColors.primary;
  const errorColor = '#FF0000';

  const headers = {
    'Content-Language': legalPersonalitySlug,
    Authorization: `Bearer ${token}`
  };

  const validationSchema = Yup.object().shape({
    shareholders: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().nullable(), // Campo `id` opcional
        name: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        occupation: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        documentType: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value;
            }
          }),
        document_number: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value;
            }
          }),
        gender: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        dateOfBirth: Yup.date()
          .transform((value, originalValue) => {
            return originalValue ? new Date(originalValue) : null;
          })
          .max(eighteenYearsAgo, t('validations.valMinAge') || '')
          .typeError(t('validations.valInvalidDate') || '')
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        phone: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value == null ? '' : String(value))) // Normaliza valores null ou undefined
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        ddi: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value == null ? '' : String(value))) // Normaliza valores null ou undefined
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        email: Yup.string()
          .email(t('OnboardingCreateAccountErrorEmailMustBe') || '')
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        nationality: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        passportNo: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        expiryDate: Yup.date()
          .transform((value, originalValue) => {
            return originalValue ? new Date(originalValue) : null;
          })
          .min(sixMonthsFromNow, t('validations.valExpiryDate') || '')
          .typeError(t('validations.valInvalidDate') || '')
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        passportIssueCountry: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        issueDate: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        address1: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        postalCode: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        city: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        state: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        country: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value; // Valida apenas se for o accordion ativo
            }
          }),
        addressProofOwner: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return !isCurrent || !!value;
            }
          }),
        addressProofKinship: Yup.string()
          .nullable()
          .notRequired()
          .test({
            name: 'required-when-other',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              if (!isCurrent) return true;
              const owner = (this.parent as any)?.addressProofOwner;
              return owner !== 'other' || !!value;
            }
          }),
        passport: Yup.mixed()
          .nullable()
          .notRequired()
          .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', (value) =>
            isAllowedShareholderFileType(value)
          )
          .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', (value) =>
            isAllowedShareholderFileSize(value)
          )
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return (
                !isCurrent || hasShareholderDocument(value, (this.parent as UserInfos)?.passportExistingName)
              );
            }
          }),
        document1: Yup.mixed()
          .nullable()
          .notRequired()
          .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', (value) =>
            isAllowedShareholderFileType(value)
          )
          .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', (value) =>
            isAllowedShareholderFileSize(value)
          )
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return (
                !isCurrent || hasShareholderDocument(value, (this.parent as UserInfos)?.document1ExistingName)
              );
            }
          }),
        proveAddress: Yup.mixed()
          .nullable()
          .notRequired()
          .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', (value) =>
            isAllowedShareholderFileType(value)
          )
          .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', (value) =>
            isAllowedShareholderFileSize(value)
          )
          .test({
            name: 'required-current-only',
            message: t('Required') || '',
            test: function (value) {
              const match = this.path.match(/shareholders\[(\d+)\]/);
              const currentAccordionIndex = match ? parseInt(match[1], 10) : NaN;
              const isCurrent = this.options.context?.activeShareholderIndex === currentAccordionIndex;
              return (
                !isCurrent || hasShareholderDocument(value, (this.parent as UserInfos)?.proveAddressExistingName)
              );
            }
          })
      })
    )
  });

  const context = {
    shareholders: shareholdersData,
    activeShareholderIndex,
    totalShares: company?.total_issued_shares || 50000
  };

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    trigger,
    control,
    watch,
    reset
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    context: context,
    defaultValues: {
      shareholders: shareholdersData?.shareholders || []
    },
    mode: 'onSubmit'
  });

  const triggerWithContext = (index: number) => {
    return trigger(`shareholders.${index}`, { shouldFocus: true });
  };

  useFieldArray<FormValues>({ control, name: 'shareholders' });

  // Re-validate on language change only after errors already exist (never on mount)
  const prevLanguageRef = useRef(i18n.language);
  useEffect(() => {
    if (prevLanguageRef.current === i18n.language) return;
    prevLanguageRef.current = i18n.language;
    if (Object.keys(errors).length > 0) {
      void trigger();
    }
  }, [i18n.language, trigger, errors]);

  const handleIndividualSubmit = (index: number) => {
    triggerWithContext(index).then((isValid) => {
      if (isValid) {
        handleSubmit((data) => {
          const shareholderData = data.shareholders[index];
          handleSubmitIndividualForm(index, shareholderData);
        })();
      } else {
        console.warn(`${t('validations.valValidationFailed')} ${t('validations.valFieldsEmpty')}`);
        errorToast(`${t('validations.valValidationFailed')} ${t('validations.valFieldsEmpty')}`);
      }
    });
  };

  const handleSubmitIndividualForm = async (index: number, data: FormValues['shareholders'][number]) => {
    if (!company || !company.slug) {
      console.error('Company or company.slug is not defined');
      return;
    }
    try {
      if (await triggerShareholderFields(activeShareholderIndex)) {
        setLoading(true);

        const payload = {
          ...data,
          expiryDate: formatDateToYYYYMMDD(data.expiryDate as unknown as Date),
          dateOfBirth: formatDateToYYYYMMDD(data.dateOfBirth as unknown as Date),
          company_slug: company!.slug,
          is_director: 1
        };

        const getIdShareholders =
          shareholdersData.length > index && shareholdersData[index]?.id ? shareholdersData[index].id : null;
        
        if (getIdShareholders) {
          const payloadPut = {
            ...payload,
            passport: data.passport || undefined, // Remova o campo se estiver vazio
            address: data.proveAddress || undefined, // Remova o campo se estiver vazio
            document1: data.document1 || undefined // Remova o campo se estiver vazio
          };

          // PUT request
          await ShareholdersRegisteringService.updateShareholders(api, getIdShareholders, payloadPut, { headers });
          shareholdersData[index] = { ...data, id: getIdShareholders }; // Atualize o acionista específico
          await registerShareholderDocuments(getIdShareholders, data);
        } else {
          // POST request
          const response = await ShareholdersRegisteringService.registerShareholders(api, payload, { headers });
          const shareholderID = response?.data?.id;
          
          if (!shareholderID) throw new Error('Failed to register shareholder');

          // Atualize shareholdersData
          shareholdersData[index] = { ...data, id: shareholderID }; // Adicione o novo acionista

          await registerShareholderDocuments(shareholderID, data);
        }

        // Atualiza o estado dos acionistas e os acordes
        setShareholdersData([...shareholdersData]);
        await getNumbersDirectors();
        updateAccordionStates();

        setLoading(false);
      }
    } catch (error: any) {
      console.error('Error registering shareholder:', error);
      const msgError = error?.response?.data?.error as string;
      errorToast(msgError);
    } finally {
      setLoading(false);
    }
  };

  // Função para registrar os documentos
  const registerShareholderDocuments = async (shareholderID: number, data: FormValues['shareholders'][number]) => {
    const header = {
      ...headers,
      'Content-Type': 'multipart/form-data'
    };
    const documentPayload: registerDocumentsShareholders = {
      passport: data.passport instanceof File ? data.passport : null,
      document1: data.document1 instanceof File ? data.document1 : null,
      prove_address: data.proveAddress instanceof File ? data.proveAddress : null
    };

    if (documentPayload.passport || documentPayload.document1 || documentPayload.prove_address) {
      await ShareholdersRegisteringService.registerDocumentsShareholders(api, shareholderID, documentPayload, {
        headers: header
      });
      console.log(`Documentos registrados para o acionista ${shareholderID}.`);
    } else {
      console.error(`Nenhum documento a registrar para o acionista ${shareholderID}.`);
    }
  };

  // Função para atualizar o nome do arquivo de cada acionista
  const handleFileChange = (file: any, index: number, fieldName: string, field: any) => {
    if (file) {
      field.onChange(file); // Atualiza o React Hook Form
      setFileNames((prev) => ({
        ...prev,
        [`${fieldName}-${index}`]: file.name
      }));
    }
  };

  const UploadCard = ({
    label,
    inputId,
    fileName,
    errorMessage,
    inputRef,
    onFileSelect,
    mockup
  }: {
    label: string;
    inputId: string;
    fileName?: string;
    errorMessage?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    onFileSelect: (file?: File) => void;
    mockup?: 'passport' | 'identity';
  }) => (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-sm font-medium text-slate-900">{label}</p>
            {mockup ? <DocumentMockup type={mockup} /> : null}
          </div>
          <p className="text-xs text-slate-500">{t('labels.lblDocumentsSupported')}</p>
        </div>
        {fileName ? (
          <span className="max-w-[180px] truncate rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700">
            {fileName}
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">PDF/DOCX</span>
        )}
      </div>
      <label className="group flex h-[140px] w-full cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-white transition hover:border-blue-300 hover:bg-blue-50/40">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <FiUploadCloud size={20} />
          </div>
          <span className="text-sm font-medium text-slate-700">{t('labels.lblPushYourDocument')}</span>
          <span className="text-xs text-slate-500">{t('labels.lblOrSearchInYourComputer')}</span>
        </div>
        <input
          id={inputId}
          type="file"
          className="hidden"
          ref={inputRef}
          onChange={(e) => onFileSelect(e.target.files?.[0])}
        />
      </label>
      {errorMessage ? <span className="text-xs text-red-500">{errorMessage}</span> : null}
    </div>
  );

  const updateAccordionStates = () => {
    const currentAccordionIndex = parseInt(currentAccordion.split('-')[1]);

    setAccordionStates((prev: any) =>
      prev.map((item: any, index: number) => {
        if (index === currentAccordionIndex) {
          return AccordionState.VALIDATED;
        } else if (index === currentAccordionIndex + 1) {
          return AccordionState.CURRENT;
        }
        return item;
      })
    );

    setCurrentAccordion('item-' + (currentAccordionIndex + 1));
  };

  const getNumbersDirectors = async () => {
    try {
      const { data } = await AccountService.getPlan(api, company?.slug, {
        headers
      });
      console.log(data.directors);
      // Aguarda que todos os dados dos acionistas sejam mapeados, incluindo os arquivos como File
      const formattedShareholders = await Promise.all(data.directors.map(mapShareholderDataToUserInfos));

      setShareholdersData(formattedShareholders || []);
      setNumberDirectors(data.number_of_directors);
      setNumberRegistredDirectors(data.number_of_registred_directors);

      initializeAccordionStates(data.number_of_directors, data.number_of_registred_directors);
    } catch (e) {
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  const initializeAccordionStates = (numShareholders: number, numRegisteredShareholders: number) => {
    const validatedArray = new Array(numRegisteredShareholders).fill(AccordionState.VALIDATED);
    const pendingArray = new Array(numShareholders - numRegisteredShareholders).fill(AccordionState.PENDING);

    setAccordionStates([...validatedArray, ...pendingArray]);

    // Logic to find the first non-VALIDATED accordion state
    const firstNonValidatedIndex = [...validatedArray, ...pendingArray].findIndex(
      (item) => item !== AccordionState.VALIDATED
    );

    if (firstNonValidatedIndex !== -1 && firstNonValidatedIndex !== 0) {
      setCurrentAccordion('item-' + firstNonValidatedIndex);
      setAccordionStates((prev: any) => {
        return prev.map((item: any, index: number) => {
          if (index === firstNonValidatedIndex) {
            return AccordionState.CURRENT;
          }
          return item;
        });
      });
    }
  };

  // Função para gerar dinamicamente os caminhos dos campos e validar com o trigger
  const triggerShareholderFields = async (index: number) => {
    // Cria uma lista de caminhos específicos para o acionista
    const fieldPaths = shareholderFields.map((field) => `shareholders.${index}.${field}` as const);

    // Chama trigger com a lista de caminhos de campo
    const isValid = await trigger(fieldPaths);
    return isValid;
  };

  // Função para salvar o Step Members quando finalizado
  // const handleSaveStepDirectors = async () => {
  //   console.log('diretores',numberRegistredDirectors);
  //   console.log('diretores 2',numberDirectors);

  //   if (numberRegistredDirectors === numberDirectors) {
  //     const response = await ShareholdersRegisteringService.advancingToTheNextStep(api, null, { headers });
  //     if (response.success === false) {
  //       console.error('Failed to advance to the next step Business', response.error);
  //     }
  //     setStep(Steps.Shareholders);
  //   } else {
  //     errorToast(`${t('errorMessage.errMessStepRegisterMembers')}`);
  //   }
  // };

  const handleSaveStepDirectors = async () => {
    // console.log('diretores cadastrados:', numberRegistredDirectors);
    // console.log('diretores necessários:', numberDirectors);
    // console.log(numberRegistredDirectors === numberDirectors);

    try {
      if (numberRegistredDirectors === numberDirectors) {
        const response = await ShareholdersRegisteringService.advancingToTheNextStep(api, null, { headers });

        if (!response || response.success === false) {
          console.error('Erro ao avançar para a próxima etapa:', response?.error || 'Resposta indefinida');
          errorToast(t('errorMessage.errMessStepRegisterMembers') || 'Erro ao avançar para a próxima etapa.');
          return;
        }

        setStep(Steps.Shareholders);
      } else {
        errorToast(t('errorMessage.errMessStepRegisterMembers') || 'Número de diretores não corresponde ao esperado.');
      }
    } catch (error) {
      console.error('Erro inesperado ao salvar a etapa de diretores:', error);
      errorToast(t('errorMessage.errMessUnexpected') || 'Ocorreu um erro inesperado. Tente novamente.');
    }
  };


  useEffect(() => {
    if (!shareholdersData.length) {
      getNumbersDirectors();
    }
  }, []);

  useEffect(() => {
    if (!numberDirectors) {
      return;
    }

    const { shareholders, fileNames: nextFileNames } = buildRegisteringPeopleFormValues(
      shareholdersData || [],
      numberDirectors
    );
    reset({ shareholders });
    setFileNames(nextFileNames);
  }, [shareholdersData, numberDirectors, reset]);

  // Cria um array temporário para renderização inicial
  const tempFields = Array.from({ length: numberDirectors || 0 }, (_, index) => ({
    id: `temp-${index}`,
    index
  }));

  return (
    <div className="flex flex-col gap-6 pb-8">
      <PageHeadline title={t('others.othRegistering')} />
      <div className="lg:10/12 flex w-full flex-row flex-wrap items-center justify-between gap-8 lg:flex-row">
        <StepperItem text={t('others.othCompany')} step={1} active onClick={() => setStep(Steps.Company)} />
        <StepperItem text={t('others.othDirectors')} step={2} active />
        <StepperItem text={t('others.othShareholders')} step={3} />
        <StepperItem text={t('others.othBusiness')} step={4} />
      </div>
      <Accordion.Root
        className="mt-10 rounded-md bg-mauve6 shadow-[0_2px_10px] shadow-black/5"
        type="single"
        defaultValue={currentAccordion}
        value={currentAccordion}
        onValueChange={(value) => {
          const newIndex = parseInt(value.split('-')[1]);
          setActiveShareholderIndex(newIndex); // Define o acionista ativo
          setCurrentAccordion(value);
        }}
        collapsible
      >
        {tempFields.map((field, index) => (
          <AccordionItem value={`item-${index}`} key={index}>
            <AccordionTrigger>{`${t('labels.lblDirector')} ${index === 0 ? 1 : index + 1}`}</AccordionTrigger>
            <AccordionContent className="bg-slate-50/60">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  triggerWithContext(index).then((isValid: boolean) => {
                    if (isValid) {
                      handleSubmit((data) => handleSubmitIndividualForm(index, data.shareholders[index]))();
                    }
                  });
                }}
              >
                <div className="flex flex-col items-start justify-between gap-6 md:flex-row">
                  <DashboardContainer title={t('labels.lblInputInformationDirectors') || ''}>
                    <Controller
                      name={`shareholders.${index}.id`}
                      control={control}
                      defaultValue={typeof field.id === 'number' ? field.id : 0}
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {t('labels.lblDirector')}
                      </span>
                      <ScanPassportControl index={index} setValue={setValue} primaryColor={primaryColor} />
                    </div>
                      <Controller
                        key={`name-${index}`}
                        name={`shareholders.${index}.name`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.name`)}
                        render={({ field }) => (
                          <Input
                            {...field}
                            ref={field.ref}
                            label={t('labels.lblFirstName') || ''}
                            error={activeShareholderIndex === index ? errors?.shareholders?.[index]?.name?.message : ''}
                          />
                        )}
                      />
                      <Controller
                        key={`occupation-${index}`}
                        name={`shareholders.${index}.occupation`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.occupation`)}
                        render={({ field }) => (
                          <Input
                            {...field}
                            ref={field.ref}
                            label={t('labels.lblOccupation') || ''}
                            error={
                              activeShareholderIndex === index ? errors?.shareholders?.[index]?.occupation?.message : ''
                            }
                          />
                        )}
                      />
                      <Controller
                        key={`documentType-${index}`}
                        name={`shareholders.${index}.documentType`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.documentType`) || undefined}
                        render={({ field: documentTypeField }) => (
                          <div className="flex w-full flex-col gap-1">
                            <label
                              htmlFor={`documentType-${index}`}
                              className={`${
                                errors?.shareholders?.[index]?.documentType?.message
                                  ? `text-${errorColor}`
                                  : `text-${primaryColor}`
                              } text-sm`}
                            >
                              {t('labels.lblDocumentType')}
                            </label>
                            <select
                              id={`documentType-${index}`}
                              className={`${
                                errors?.shareholders?.[index]?.documentType ? 'border-red-500' : 'border-[#CED4DA]'
                              } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                              {...documentTypeField}
                            >
                              <option value="" disabled>
                                {t('labels.lblSelect')}
                              </option>
                              <option value="national_id">{t('labels.lblNationalIDPlain')}</option>
                              <option value="drivers_license">{t('labels.lblDriverLicence')}</option>
                            </select>
                            <span className="h-0 text-xs text-red-500">
                              {errors?.shareholders?.[index]?.documentType?.message}
                            </span>
                          </div>
                        )}
                      />
                      <Controller
                        key={`document_number-${index}`}
                        name={`shareholders.${index}.document_number`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.document_number`)}
                        render={({ field }) => (
                          <Input
                            {...field}
                            ref={field.ref}
                            label={
                              watch(`shareholders.${index}.documentType`) === 'drivers_license'
                                ? `${t('labels.lblDriverLicence')} *`
                                : t('labels.lblNationalID') || ''
                            }
                            error={
                              activeShareholderIndex === index
                                ? errors?.shareholders?.[index]?.document_number?.message
                                : ''
                            }
                          />
                        )}
                      />
                      <div className="flex items-center justify-between gap-2">
                        <Controller
                          key={`gender-${index}`}
                          name={`shareholders.${index}.gender`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.gender`)}
                          render={({ field }) => (
                            <div className="flex w-full flex-col gap-1">
                              <label
                                htmlFor="gender"
                                className={`${errors?.shareholders?.[index]?.gender?.message
                                  ? `text-${errorColor}`
                                  : `text-${primaryColor}`
                                  } text-sm`}
                              >
                                {t('labels.lblGender')}
                              </label>
                              <select
                                id="gender"
                                className={`${errors?.shareholders?.[index]?.gender ? 'border-red-500' : 'border-[#CED4DA]'
                                  } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                                {...field}
                                defaultValue={getValues(`shareholders.${index}.gender`) || ''}
                              >
                                <option value="" disabled>
                                  {t('labels.lblSelect')}
                                </option>
                                <option value="male">{t('labels.lblGenderMale')}</option>
                                <option value="female">{t('labels.lblGenderFemale')}</option>
                              </select>
                              <span className="h-0 text-xs text-red-500">
                                {errors?.shareholders?.[index]?.gender?.message}
                              </span>
                            </div>
                          )}
                        />
                        <Controller
                          key={`dateOfBirth.${index}`}
                          name={`shareholders.${index}.dateOfBirth`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.dateOfBirth`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblDateOfBirth') || ''}
                              error={
                                activeShareholderIndex === index
                                  ? errors?.shareholders?.[index]?.dateOfBirth?.message
                                  : undefined
                              }
                              mask="9999/99/99"
                            />
                          )}
                        />
                      </div>
                      <FormPhoneInput
                        label={t('labels.lblPhone')}
                        id={`phone-${index}`}
                        name={`shareholders.${index}.phone`}
                        defaultValue={getValues(`shareholders.${index}.phone`) || ''}
                        error={activeShareholderIndex === index ? errors?.shareholders?.[index]?.phone?.message : ''}
                        initialCountryCode={countryCodeFromDdi(getValues(`shareholders.${index}.ddi`))}
                        onPhoneChange={([ddi, phone]) => {
                          setValue(`shareholders.${index}.phone`, phone || '', { shouldDirty: false });
                          setValue(`shareholders.${index}.ddi`, ddi || '', { shouldDirty: false });
                        }}
                        control={control}
                      />
                      <Controller
                        key={`email-${index}`}
                        name={`shareholders.${index}.email`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.email`)}
                        render={({ field }) => (
                          <Input
                            {...field}
                            ref={field.ref}
                            label={t('labels.lblEmail') || ''}
                            error={
                              activeShareholderIndex === index ? errors?.shareholders?.[index]?.email?.message : ''
                            }
                          />
                        )}
                      />
                      <div className="flex w-full flex-col gap-1">
                        <Controller
                          key={`nationality-${index}`}
                          name={`shareholders.${index}.nationality`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.nationality`)}
                          render={({ field }) => (
                            <div>
                              <input type="hidden" id={'index'} value={index} />
                              <label
                                htmlFor="nationality"
                                className={`${errors?.shareholders?.[index]?.nationality?.message
                                  ? `text-${errorColor}`
                                  : `text-${primaryColor}`
                                  } text-sm`}
                              >
                                {t('labels.lblNationality')}
                              </label>
                              <select
                                id="nationality"
                                className={`${errors?.shareholders?.[index]?.nationality?.message
                                  ? 'border-red-500'
                                  : 'border-[#CED4DA]'
                                  } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                                {...field} // Gerencia o valor e o onChange através do field do Controller
                                defaultValue={getValues(`shareholders.${index}.nationality`) || ''}
                              >
                                <option value="" disabled>
                                  {t('labels.lblSelect')}
                                </option>
                                {countries[i18n.language as 'pt-BR'].map((country) => (
                                  <option key={country.name} value={country.name}>
                                    {country.name}
                                  </option>
                                ))}
                              </select>
                              <span className="h-0 text-xs text-red-500">
                                {activeShareholderIndex === index
                                  ? errors?.shareholders?.[index]?.nationality?.message
                                  : ''}
                              </span>
                            </div>
                          )}
                        />
                      </div>
                      <div className="flex items-start justify-between gap-2">
                        <Controller
                          key={`passportNo-${index}`}
                          name={`shareholders.${index}.passportNo`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.passportNo`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblPassportNo') || ''}
                              error={
                                activeShareholderIndex === index
                                  ? errors?.shareholders?.[index]?.passportNo?.message
                                  : ''
                              }
                            />
                          )}
                        />
                        <Controller
                          key={`expiryDate-${index}`}
                          name={`shareholders.${index}.expiryDate`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.expiryDate`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblExpiryDate') || ''}
                              error={
                                activeShareholderIndex === index
                                  ? errors?.shareholders?.[index]?.expiryDate?.message
                                  : ''
                              }
                              mask="9999/99/99"
                              onChange={(e) => setValue(`shareholders.${index}.expiryDate`, e.target.value)}
                            />
                          )}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Controller
                          key={`passportIssueCountry-${index}`}
                          name={`shareholders.${index}.passportIssueCountry`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.passportIssueCountry`)}
                          render={({ field }) => (
                            <div className="flex w-full flex-col gap-1">
                              <input type="hidden" id={'index'} value={index} />
                              <label
                                htmlFor="passportIssueCountry"
                                className={`${errors?.shareholders?.[index]?.passportIssueCountry?.message
                                  ? `text-${errorColor}`
                                  : `text-${primaryColor}`
                                  } text-sm`}
                              >
                                {t('labels.lblPassportIssuingCountry')}
                              </label>
                              <select
                                id="passportIssueCountry"
                                className={`${errors?.shareholders?.[index]?.passportIssueCountry?.message
                                  ? 'border-red-500'
                                  : 'border-[#CED4DA]'
                                  } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                                {...field} // Gerencia o valor e o onChange através do field do Controller
                                onChange={(e) => setValue(`shareholders.${index}.passportIssueCountry`, e.target.value)}
                                defaultValue={getValues(`shareholders.${index}.nationality`) || ''}
                              >
                                <option value="" disabled>
                                  {t('labels.lblSelect')}
                                </option>
                                {countries[i18n.language as 'pt-BR'].map((country) => (
                                  <option key={country.name} value={country.name}>
                                    {country.name}
                                  </option>
                                ))}
                              </select>
                              <span className="h-0 text-xs text-red-500">
                                {activeShareholderIndex === index
                                  ? errors?.shareholders?.[index]?.passportIssueCountry?.message
                                  : ''}
                              </span>
                            </div>
                          )}
                        />
                        <Controller
                          key={`issueDate-${index}`}
                          name={`shareholders.${index}.issueDate`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.issueDate`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblIssueDate') || ''}
                              error={
                                activeShareholderIndex === index
                                  ? errors?.shareholders?.[index]?.issueDate?.message
                                  : ''
                              }
                              mask="9999/99/99"
                              value={getValues(`shareholders.${index}.issueDate`)}
                              onChange={(e) => setValue(`shareholders.${index}.issueDate`, e.target.value)}
                            />
                          )}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Controller
                          key={`address1-${index}`}
                          name={`shareholders.${index}.address1`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.address1`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblAddress1') || ''}
                              error={
                                activeShareholderIndex === index ? errors?.shareholders?.[index]?.address1?.message : ''
                              }
                            />
                          )}
                        />
                        <Controller
                          key={`address2-${index}`}
                          name={`shareholders.${index}.address2`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.address2`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblAddress2') || ''}
                              error={errors?.shareholders?.[index]?.address2?.message}
                            />
                          )}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Controller
                          key={`postalCode-${index}`}
                          name={`shareholders.${index}.postalCode`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.postalCode`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblPostalCode') || ''}
                              error={errors?.shareholders?.[index]?.postalCode?.message}
                            />
                          )}
                        />
                        <Controller
                          key={`city-${index}`}
                          name={`shareholders.${index}.city`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.city`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblCity') || ''}
                              error={errors?.shareholders?.[index]?.city?.message}
                            />
                          )}
                        />
                        <Controller
                          key={`state-${index}`}
                          name={`shareholders.${index}.state`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.state`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblState') || ''}
                              error={errors?.shareholders?.[index]?.state?.message}
                            />
                          )}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Controller
                          key={`neighborhood-${index}`}
                          name={`shareholders.${index}.neighborhood`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.neighborhood`)}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblNeighborhood') || ''}
                              error={errors?.shareholders?.[index]?.neighborhood?.message}
                            />
                          )}
                        />
                        <Controller
                          key={`country-${index}`}
                          name={`shareholders.${index}.country`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.country`)}
                          render={({ field }) => (
                            <div className="flex w-full flex-col gap-1">
                              <input type="hidden" id={'index'} value={index} />
                              <label
                                htmlFor="country"
                                className={`${errors?.shareholders?.[index]?.country?.message
                                  ? `text-${errorColor}`
                                  : `text-${primaryColor}`
                                  } text-sm`}
                              >
                                {t('labels.lblCountry')}
                              </label>
                              <select
                                id="country"
                                className={`${errors?.shareholders?.[index]?.country?.message
                                  ? 'border-red-500'
                                  : 'border-[#CED4DA]'
                                  } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                                {...field}
                                onChange={(e) => setValue(`shareholders.${index}.country`, e.target.value)}
                                defaultValue={getValues(`shareholders.${index}.nationality`) || ''}

                              >
                                <option value="" disabled>
                                  {t('labels.lblSelect')}
                                </option>
                                {countries[i18n.language as 'pt-BR'].map((country) => (
                                  <option key={country.name} value={country.name}>
                                    {country.name}
                                  </option>
                                ))}
                              </select>
                              <span className="h-0 text-xs text-red-500">
                                {errors?.shareholders?.[index]?.country?.message}
                              </span>
                            </div>
                          )}
                        />
                      </div>
                  </DashboardContainer>
                  <DashboardContainer title={t('labels.lblUploadDirectorsDocuments') || ''}>
                      <Controller
                        key={`passport-${index}`}
                        name={`shareholders.${index}.passport`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.passport`) || null}
                        render={({ field }) => (
                          <UploadCard
                            label={`${t('labels.lblPassport')} - ${t('others.othDirectors')} ${index + 1}`}
                            inputId={`passport-${index}`}
                            fileName={fileNames[`passport-${index}`]}
                            errorMessage={errors?.shareholders?.[index]?.passport?.message}
                            inputRef={field.ref}
                            mockup="passport"
                            onFileSelect={(file) => handleFileChange(file, index, 'passport', field)}
                          />
                        )}
                      />
                      <Controller
                        key={`document1-${index}`}
                        name={`shareholders.${index}.document1`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.document1`) || null}
                        render={({ field }) => (
                          <UploadCard
                            label={`${t('labels.lblNationalID')} - ${t('others.othDirectors')} ${index + 1}`}
                            inputId={`document1-${index}`}
                            fileName={fileNames[`document1-${index}`]}
                            errorMessage={errors?.shareholders?.[index]?.document1?.message}
                            inputRef={field.ref}
                            mockup="identity"
                            onFileSelect={(file) => handleFileChange(file, index, 'document1', field)}
                          />
                        )}
                      />
                      <Controller
                        key={`addressProofOwner-${index}`}
                        name={`shareholders.${index}.addressProofOwner`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.addressProofOwner`) || undefined}
                        render={({ field: ownerField }) => (
                          <div className="flex w-full flex-col gap-1">
                            <label
                              htmlFor={`addressProofOwner-${index}`}
                              className={`${
                                errors?.shareholders?.[index]?.addressProofOwner?.message
                                  ? `text-${errorColor}`
                                  : `text-${primaryColor}`
                              } text-sm`}
                            >
                              {t('labels.lblAddressProofOwner')}
                            </label>
                            <select
                              id={`addressProofOwner-${index}`}
                              className={`${
                                errors?.shareholders?.[index]?.addressProofOwner
                                  ? 'border-red-500'
                                  : 'border-[#CED4DA]'
                              } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                              {...ownerField}
                            >
                              <option value="" disabled>
                                {t('labels.lblSelect')}
                              </option>
                              <option value="self">{t('labels.lblAddressProofOwnerSelf')}</option>
                              <option value="other">{t('labels.lblAddressProofOwnerOther')}</option>
                            </select>
                            <span className="h-0 text-xs text-red-500">
                              {errors?.shareholders?.[index]?.addressProofOwner?.message}
                            </span>
                          </div>
                        )}
                      />
                      {watch(`shareholders.${index}.addressProofOwner`) === 'other' ? (
                        <Controller
                          key={`addressProofKinship-${index}`}
                          name={`shareholders.${index}.addressProofKinship`}
                          control={control}
                          defaultValue={getValues(`shareholders.${index}.addressProofKinship`) || ''}
                          render={({ field }) => (
                            <Input
                              {...field}
                              ref={field.ref}
                              label={t('labels.lblKinshipDegree') || ''}
                              error={errors?.shareholders?.[index]?.addressProofKinship?.message}
                            />
                          )}
                        />
                      ) : null}
                      <Controller
                        key={`proveAddress-${index}`}
                        name={`shareholders.${index}.proveAddress`}
                        control={control}
                        defaultValue={getValues(`shareholders.${index}.proveAddress`) || null}
                        render={({ field }) => (
                          <UploadCard
                            label={`${t('labels.lblProveAddress')} - ${t('others.othDirectors')} ${index + 1}`}
                            inputId={`proveAddress-${index}`}
                            fileName={fileNames[`proveAddress-${index}`]}
                            errorMessage={errors?.shareholders?.[index]?.proveAddress?.message}
                            inputRef={field.ref}
                            onFileSelect={(file) => handleFileChange(file, index, 'proveAddress', field)}
                          />
                        )}
                      />
                  </DashboardContainer>
                </div>
                <div className="mt-10 flex justify-end gap-4">
                  <MainCustomButton
                    text={t('buttons.btnSave')}
                    type="submit"
                    loading={loading}
                    // onClick={() => handleIndividualSubmit(index)}
                  />
                </div>
              </form>
            </AccordionContent>
          </AccordionItem>
        ))}
        <div className="h- flex justify-end gap-4 border-t-4 bg-white p-4">
          <MainCustomButton
            text={t('buttons.btnNext')}
            type="submit"
            loading={loading}
            style={{ height: 46, fontSize: 16 }}
            disabled={numberRegistredDirectors !== numberDirectors}
            onClick={() => handleSaveStepDirectors()}
          />
        </div>
      </Accordion.Root>
    </div>
  );
};

export { DashboardRegisteringDirectorsPage };
